import { Component, OnInit, HostListener } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { NavigationMenuService } from '@shared/service/app/navigation-menu.service';
import { EmpresaService } from '@shared/service/app/empresa.service';
import { AuthService } from '@core/auth/auth.service';
import { AuthUtils } from '@core/auth/auth.utils';

export interface ISearchableModule {
    label: string;          // Solo el nombre del submódulo (ej. "Costos")
    fullLabel: string;      // Nombre completo con ruta (ej. "Gestión Comercial > Costos")
    ruta: string;
    icono?: string;
    category: string;       // Categoría principal (ej. "Gestión Comercial")
    categoryIcon?: string;  // Icono de la categoría principal
    globalIndex?: number;   // Índice secuencial global para el teclado
}

export interface IGroupedModules {
    category: string;
    icono?: string;
    items: ISearchableModule[];
}

@Component({
    selector: 'app-command-palette',
    templateUrl: './command-palette.component.html',
    styleUrls: ['./command-palette.component.scss']
})
export class CommandPaletteComponent implements OnInit {
    searchQuery: string = '';
    modules: ISearchableModule[] = [];
    groupedModules: IGroupedModules[] = [];
    selectedIndex: number = 0;
    totalFilteredItemsCount: number = 0;
    color_primario: string;
    color_secundario: string;

    constructor(
        private _menuService: NavigationMenuService,
        private _router: Router,
        private _dialogRef: MatDialogRef<CommandPaletteComponent>,
        private _empresaService: EmpresaService,
        private _authService: AuthService
    ) {}

    ngOnInit(): void {
        const token = this._authService?.accessToken;
        if (!token || AuthUtils.isTokenExpired(token)) {
            this._dialogRef.close();
            return;
        }

        this._empresaService.getEmpresa();
        this._empresaService.empresa$.subscribe((empresa) => {
            if (empresa) {
                this.color_primario = empresa.color_primario;
                this.color_secundario = empresa.color_secundario;
            }
        });
        this._menuService.getMenuData().subscribe((res) => {
            if (res) {
                const menuList = Array.isArray(res) ? res : (res.menu || []);
                this.modules = this.flattenMenu(menuList);
                this.filterModules();
            }
        });
    }

    private flattenMenu(menuItems: any[], parentLabel: string = '', parentIcon: string = '', categoryName: string = ''): ISearchableModule[] {
        let result: ISearchableModule[] = [];
        for (const item of menuItems) {
            const itemDetail = item.item ? item.item : item;
            
            // Si tiene hijos, no se añade como ruta final para navegar, sino como categoría/subcategoría
            const hasChildren = (item.hijos && item.hijos.length > 0) || (itemDetail.hijos && itemDetail.hijos.length > 0);
            
            const currentCategory = parentLabel ? categoryName : itemDetail.descripcion;
            const currentLabel = parentLabel ? `${parentLabel} > ${itemDetail.descripcion}` : itemDetail.descripcion;
            const currentIcon = itemDetail.icono || itemDetail.icon || parentIcon;

            if (itemDetail.ruta && !hasChildren) {
                result.push({
                    label: itemDetail.descripcion,
                    fullLabel: currentLabel,
                    ruta: itemDetail.ruta,
                    icono: currentIcon,
                    category: currentCategory,
                    categoryIcon: parentIcon || currentIcon
                });
            }

            const children = item.hijos || itemDetail.hijos;
            if (children && children.length > 0) {
                result = result.concat(this.flattenMenu(children, currentLabel, currentIcon, currentCategory));
            }
        }
        return result;
    }

    filterModules(): void {
        this.selectedIndex = 0;
        let filtered: ISearchableModule[] = [];
        const normalizedQuery = this.normalizeText(this.searchQuery);

        if (!normalizedQuery) {
            filtered = [...this.modules];
        } else {
            const queryWords = normalizedQuery.split(/\s+/).filter(Boolean);
            filtered = this.modules.filter((m) => {
                const targetText = this.normalizeText(`${m.category || ''} ${m.fullLabel || m.label || ''}`);
                return queryWords.every((word) => targetText.includes(word));
            });
        }

        // Agrupar por categoría y asignar índices globales
        const groupsMap = new Map<string, IGroupedModules>();
        let globalIdx = 0;

        for (const mod of filtered) {
            mod.globalIndex = globalIdx++;
            const categoryKey = mod.category || 'Módulos';
            if (!groupsMap.has(categoryKey)) {
                groupsMap.set(categoryKey, {
                    category: categoryKey,
                    icono: mod.categoryIcon,
                    items: []
                });
            }
            groupsMap.get(categoryKey).items.push(mod);
        }

        this.groupedModules = Array.from(groupsMap.values());
        this.totalFilteredItemsCount = globalIdx;
    }

    navigate(module: ISearchableModule): void {
        if (module.ruta) {
            this._router.navigate([module.ruta]);
            this._dialogRef.close();
        }
    }

    @HostListener('keydown', ['$event'])
    handleKeyboardEvent(event: KeyboardEvent) {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            if (this.totalFilteredItemsCount > 0) {
                this.selectedIndex = (this.selectedIndex + 1) % this.totalFilteredItemsCount;
            }
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            if (this.totalFilteredItemsCount > 0) {
                this.selectedIndex = (this.selectedIndex - 1 + this.totalFilteredItemsCount) % this.totalFilteredItemsCount;
            }
        } else if (event.key === 'Enter') {
            event.preventDefault();
            const selectedItem = this.findItemByGlobalIndex(this.selectedIndex);
            if (selectedItem) {
                this.navigate(selectedItem);
            }
        } else if (event.key === 'Escape') {
            this._dialogRef.close();
        }
    }

    private findItemByGlobalIndex(index: number): ISearchableModule | null {
        for (const group of this.groupedModules) {
            for (const item of group.items) {
                if (item.globalIndex === index) {
                    return item;
                }
            }
        }
        return null;
    }

    private normalizeText(value: any): string {
        if (!value && value !== 0) {
            return '';
        }
        return value
            .toString()
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .trim();
    }
}
